import React, { useEffect, useState, useCallback } from 'react';
import { IconSearch, IconChevronDown, IconX } from '@tabler/icons-react';
import { apiClient } from '../../../../../config/apiConfig';
import toast from 'react-hot-toast';

const STAGE_TITLES = [
 ['waiting_to_start', 'Waiting to Start'],
 ['recruitment', 'Recruitment'],
 ['live', 'Live'],
 ['payment_report', 'Payment & Reports'],
 ['finished', 'Finished']
];

const ExcludeCreatorsMenu = ({
 anchorRef,
 open,
 onClose,
 onApply,
 initialSelectedCampaigns = [],
 hideCampaignId = null
}) => {
 const [campaigns, setCampaigns] = useState({
 waiting_to_start: [],
 recruitment: [],
 live: [],
 payment_report: [],
 finished: []
 });
 const [searchQuery, setSearchQuery] = useState('');
 const [selectedCampaigns, setSelectedCampaigns] = useState(initialSelectedCampaigns);
 const [loading, setLoading] = useState(false);
 const [expandedSections, setExpandedSections] = useState({
 recruitment: true,
 live: true,
 waiting_to_start: false,
 payment_report: false,
 finished: false
 });

 const fetchCampaigns = useCallback(async (search = '') => {
 try {
 setLoading(true);
 const response = await apiClient.get('/campaigns/for-exclusion', {
 params: search ? { search } : {}
 });
 if (response.data?.success) {
 setCampaigns(response.data.data);
 }
 } catch (error) {
 console.error('Error fetching campaigns for exclusion:', error);
 toast.error('Failed to load campaigns');
 } finally {
 setLoading(false);
 }
 }, []);

 useEffect(() => {
 if (open) fetchCampaigns();
 }, [open, fetchCampaigns]);

 useEffect(() => {
 if (!open) return;
 const timer = setTimeout(() => fetchCampaigns(searchQuery), 300);
 return () => clearTimeout(timer);
 }, [searchQuery, open, fetchCampaigns]);

 useEffect(() => {
 if (open) setSelectedCampaigns(initialSelectedCampaigns);
 }, [open, initialSelectedCampaigns]);

 const handleToggle = (campaignId) => {
 setSelectedCampaigns(prev =>
 prev.includes(campaignId)
 ? prev.filter(id => id !== campaignId)
 : [...prev, campaignId]
 );
 };

 const handleApply = () => {
 onApply(selectedCampaigns);
 onClose();
 };

 const handleClear = () => {
 setSelectedCampaigns([]);
 setSearchQuery('');
 onApply([]);
 };

 const toggleSection = (key) => {
 setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
 };

 if (!open) return null;

 const anchorRect = anchorRef?.current?.getBoundingClientRect();
 const menuStyle = anchorRect
 ? {
 top: anchorRect.bottom + 4,
 left: Math.max(8, Math.min(anchorRect.left, window.innerWidth - 408))
 }
 : { top: '50%', left: '50%' };

 const renderSection = (title, list, key) => {
 const filtered = hideCampaignId ? list.filter(c => c._id !== hideCampaignId) : list;
 if (filtered.length === 0) return null;
 const isExpanded = expandedSections[key];
 return (
 <div key={key} className="border-b border-glass-border last:border-b-0">
 <button
 type="button"
 onClick={() => toggleSection(key)}
 className="w-full px-3 py-2 flex items-center justify-between hover:bg-glass-bg-hover"
 >
 <span className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
 {title} ({filtered.length})
 </span>
 <IconChevronDown
 size={14}
 className={`text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`}
 />
 </button>
 {isExpanded && (
 <div className="pb-1">
 {filtered.map(campaign => {
 const onboardedCount = (campaign.creators || []).filter(c =>
 c.briefingStatus === 'ONBOARDED' || c.briefingStatus === 'briefing_accepted'
 ).length;
 const selected = selectedCampaigns.includes(campaign._id);
 return (
 <button
 key={campaign._id}
 type="button"
 onClick={() => handleToggle(campaign._id)}
 className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-glass-bg-hover ${selected ? 'bg-accent-muted/30' : ''}`}
 >
 <input
 type="checkbox"
 checked={selected}
 onChange={() => {}}
 className="border-glass-border text-accent focus:ring-accent"
 />
 <div className="flex-1 min-w-0">
 <span className="text-sm text-text-primary block truncate">{campaign.name}</span>
 <span className="text-xs text-text-tertiary">{onboardedCount} onboarded</span>
 </div>
 </button>
 );
 })}
 </div>
 )}
 </div>
 );
 };

 const hasResults = Object.values(campaigns).some(arr =>
 (hideCampaignId ? arr.filter(c => c._id !== hideCampaignId) : arr).length > 0
 );

 return (
 <>
 <div className="fixed inset-0 z-40" onClick={onClose} />
 <div
 className="ui-motion-menu fixed z-50 bg-surface-1 border border-glass-border w-[400px] max-w-[calc(100vw-16px)] max-h-[600px] overflow-hidden flex flex-col"
 data-origin="top-left"
 style={menuStyle}
 >
 <div className="px-3 pt-3 pb-2 border-b border-glass-border">
 <div className="flex items-center gap-2 mb-2">
 <h4 className="text-sm font-semibold text-text-primary flex-1">
 Exclude creators by campaign
 </h4>
 <button
 type="button"
 onClick={onClose}
 className="text-text-tertiary hover:text-text-primary"
 aria-label="Close"
 >
 <IconX size={16} />
 </button>
 </div>
 <div className="relative">
 <IconSearch size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary" />
 <input
 type="text"
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 placeholder="Search campaigns..."
 className="w-full pl-8 pr-2 py-1.5 text-sm border border-glass-border bg-surface-1 text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-accent focus:border-transparent"
 />
 </div>
 </div>

 <div className="flex-1 overflow-y-auto">
 {loading && (
 <div className="px-3 py-6 text-center text-sm text-text-secondary">Loading...</div>
 )}
 {!loading && !hasResults && (
 <div className="px-3 py-6 text-center text-sm text-text-secondary">No campaigns found</div>
 )}
 {!loading && hasResults && STAGE_TITLES.map(([key, title]) =>
 renderSection(title, campaigns[key] || [], key)
 )}
 </div>

 <div className="px-3 py-2 border-t border-glass-border flex items-center gap-2">
 <button
 type="button"
 onClick={handleClear}
 disabled={selectedCampaigns.length === 0}
 className="px-3 py-1.5 text-sm border border-glass-border text-text-primary hover:bg-glass-bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
 >
 Clear
 </button>
 <button
 type="button"
 onClick={handleApply}
 className="flex-1 px-3 py-1.5 text-sm bg-accent hover:bg-accent-hover text-white"
 >
 Apply{selectedCampaigns.length > 0 ? ` (${selectedCampaigns.length})` : ''}
 </button>
 </div>
 </div>
 </>
 );
};

export default ExcludeCreatorsMenu;
